<script setup>
import { reactive, computed } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { KkInfo } from '../../../components/ui'
import { KkIcon, KkMore, KkLoading, KkButton } from '../../../components/base'
import { useI18n } from 'vue-i18n'
import replyTemplatesImagePath from '../../../../../images/replyTemplates/reply_templates'

const { t } = useI18n()
const store = useStore()
const loading = computed(()=> store.state.companiesReplyTemplates.loading || store.state.companiesReplyTemplates.loading,)
const { company_uuid: companyUuid } = useRoute().params
const state = reactive(
  {
    moreDisplayLength: 120
  }
)

const readReplyTemplates = () => store.dispatch('companiesReplyTemplates/readReplyTemplates')
const deleteReplyTemplate = ({ reply_template_id: replyTemplateId }) => store.dispatch('companiesReplyTemplates/deleteReplyTemplate',{ reply_template_id: replyTemplateId })

const replyTemplates = computed(() => store.state.companiesReplyTemplates.data)

const onClickDeleteReplyTemplate = (replyTemplateId) => {
  const message = t('messages.confirmToDelete')
  if (confirm(message)) {
    deleteReplyTemplate({ reply_template_id: replyTemplateId })
  }
}

const load = async () => {
  await readReplyTemplates()
}

load()
</script>

<template>
  <div>
    <div class="p-box">
      <KkLoading v-if="loading" />
      <div v-else-if="replyTemplates.length === 0" class="p-box__body">
        <div class="noReplyTemplates__notice">
          {{ t('pages.companies.management.noReplyTemplates.notice') }}
        </div>
        <div class="p-box">
          <div class="p-box__header">
            <h3 class="p-box__title">{{ t('pages.companies.management.noReplyTemplates.title') }}</h3>
          </div>
          <div class="p-box__body">
            <div>
              <div class="noReplyTemplates__topSection">
                <div class="noReplyTemplates__image">
                  <img :src="replyTemplatesImagePath" alt="">
                </div>
                <div class="noReplyTemplates__description">
                  {{ t('pages.companies.management.noReplyTemplates.description') }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="linkToAddReplyTemplates">
          <div class="c-button c-button--right">
            <router-link
              :to="{ name: 'CompaniesManagement', params: { company_uuid: companyUuid } }"
              class="c-button__item c-button__item--outline-cancel"
            >
              {{ $t('labels.cancel') }}
            </router-link>
            <router-link :to="{ name: 'CompaniesManagementReplyTemplatesNew', params: { company_uuid: companyUuid }}"
                         class="c-button__item c-button__item--primary"
            >
              {{ t('pages.companies.management.noReplyTemplates.add') }}
            </router-link>
          </div>
        </div>
      </div>
      <div v-else class="p-box__body">
        <KkInfo>
          <p>{{ t('pages.companies.management.replyTemplates.info') }}</p>
        </KkInfo>
        <div class="p-table">
          <div class="p-table__content">
            <table>
              <thead>
                <tr>
                  <th>
                    <span>{{ t('models.replyTemplate.replyTemplateName') }}</span>
                  </th>
                  <th>
                    <span>{{ t('models.replyTemplate.replyTemplateContent') }}</span>
                  </th>
                  <th />
                </tr>
                <tr class="p-table__content__space">
                  <td colspan="5" />
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in replyTemplates" :key="item.id">
                  <td><span>{{ item.name }}</span></td>
                  <td class="contentColumn">
                    <div class="content"><KkMore :text="item.content" :length="state.moreDisplayLength" /></div>
                  </td>
                  <td>
                    <div class="flexContainer">
                      <router-link :to="{ name: 'CompaniesManagementReplyTemplatesEdit', params: { company_uuid: companyUuid, reply_template_id: item.id }}" class="action">
                        <KkIcon name="iconPencil" class="action__icon" />
                        {{ $t('labels.edit') }}
                      </router-link>
                      <a class="action" @click="onClickDeleteReplyTemplate(item.id)">
                        <KkIcon name="iconCross" class="action__icon" />
                        {{ $t('labels.delete') }}
                      </a>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="c-button c-button--right">
            <KkButton :text="$t('labels.add', { value: $t('models.replyTemplate.modelName') })" :to="{ name: 'CompaniesManagementReplyTemplatesNew', params: { currentCompanyUuid: companyUuid }}" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
@import '../../../../../stylesheets/biz_spa/foundation/_var.scss';

.flexContainer {
  display: flex;
  justify-content: flex-end;
  @media screen and (max-width: 768px) {
    display: block;
  }
}
.action {
  display: flex;
  align-items: flex-start;
  white-space: nowrap;
  margin-right: 24px;
  font-size: 12px;
  text-decoration: none;
  cursor: pointer;
  @media screen and (max-width: 768px) {
    margin: 5px;
    width: 80px;
  }
  &__icon {
    flex-basis: 18px;
    min-width: 18px;
    margin-right: 8px;
    img {
      display: block;
      width: 100%;
    }
  }
}
.p-table__content tbody th:not(:first-child), .p-table__content tbody td:not(:first-child) {
  border-left: none;
}
.noReplyTemplates {
  &__topSection {
    display: flex;
    margin-bottom: 24px;
    @media screen and (max-width: 768px) {
      display: block;
    }
  }
  &__image {
    max-width: 320px;
    & img {
      display: block;
    }
  }
  &__description {
    margin: auto auto auto 40px;
    font-size: 16px;
    white-space: pre-wrap;
    @media screen and (max-width: 768px) {
      margin: 20px;
    }
  }
  &__notice {
    white-space: pre-wrap;
  }
}
.linkToAddReplyTemplates {
  margin-top: 15px;
}
.contentColumn {
  width: 60%;
}
.content {
  white-space: pre-wrap;
  word-break: break-all;
  @media screen and (max-width: 768px) {
    width: 100%;
  }
}
</style>
